import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, BookOpen, GraduationCap, BarChart3, LogOut,
  Plus, Trash2, Pencil, Search, FileText, Video, X, Check,
  MessageSquare, Target, Activity, ChevronDown, Trophy, Loader2,
  School, UserCheck, ShieldCheck, Building2, Crown, Star, Award,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { ContestsManager } from '@/components/Contests';
import { PasswordChangeField } from '@/components/PasswordChangeField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'dashboard', label: 'الإحصائيات', icon: BarChart3 },
  { id: 'students', label: 'الطلاب', icon: Users },
  { id: 'directorates', label: 'المديريات', icon: Building2 },
  { id: 'schools', label: 'المدارس', icon: School },
  { id: 'ministers', label: 'الوزراء', icon: Star },
  { id: 'directors', label: 'مديرو المديريات', icon: Crown },
  { id: 'principals', label: 'مديرو المدارس', icon: ShieldCheck },
  { id: 'teachers', label: 'المعلمون', icon: UserCheck },
  { id: 'subjects', label: 'المواد الدراسية', icon: GraduationCap },
  { id: 'lessons', label: 'الدروس والمحتوى', icon: BookOpen },
  { id: 'olympics', label: 'الأولمبياد', icon: Trophy },
  { id: 'contests', label: 'المسابقات', icon: Award },
];

export default function AdminPage() {
  const { user, logout, getAuthHeaders, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/grades');
    }
  }, [user, navigate]);

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#0f0f23] text-white" dir="rtl">
      <header className="bg-[#1a1a2e]/80 backdrop-blur-md border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold">لوحة التحكم الإدارية</h1>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/grades')} className="text-[#4da6ff] hover:text-[#3d96ef] text-xs">
              <BookOpen className="w-4 h-4 ml-1" />
              تصفح الصفوف
            </Button>
            <span className="text-white/50 text-sm hidden sm:block">{user.username}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="text-red-400 hover:text-red-300">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'bg-[#4da6ff] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'dashboard' && <DashboardTab headers={getAuthHeaders()} />}
        {activeTab === 'students' && <StudentsTab headers={getAuthHeaders()} />}
        {activeTab === 'directorates' && <DirectoratesTab headers={getAuthHeaders()} />}
        {activeTab === 'schools' && <SchoolsTab headers={getAuthHeaders()} />}
        {activeTab === 'ministers' && <MinistersTab headers={getAuthHeaders()} />}
        {activeTab === 'directors' && <DirectorsTab headers={getAuthHeaders()} />}
        {activeTab === 'principals' && <PrincipalsTab headers={getAuthHeaders()} />}
        {activeTab === 'teachers' && <TeachersTab headers={getAuthHeaders()} />}
        {activeTab === 'subjects' && <SubjectsTab headers={getAuthHeaders()} />}
        {activeTab === 'lessons' && <LessonsTab headers={getAuthHeaders()} />}
        {activeTab === 'olympics' && <OlympicsTab headers={getAuthHeaders()} />}
        {activeTab === 'contests' && <ContestsManager headers={getAuthHeaders()} token={token} />}
      </div>
    </div>
  );
}

// ─── Dashboard Tab ───
function DashboardTab({ headers }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/stats`, { headers }).then(r => setStats(r.data)).catch(() => {});
  }, [headers]);

  if (!stats) return <div className="text-white/30 text-center py-12">جارٍ التحميل...</div>;

  const TT = { background: '#0f1117', border: '1px solid #ffffff15', borderRadius: 8, color: '#fff', fontSize: 12 };
  const cards = [
    { label: 'إجمالي الطلاب', value: stats.total_students, icon: Users, color: 'text-[#4da6ff]' },
    { label: 'طلاب نشطون', value: stats.active_students, icon: Activity, color: 'text-emerald-400' },
    { label: 'المواد الدراسية', value: stats.total_subjects, icon: GraduationCap, color: 'text-purple-400' },
    { label: 'الدروس', value: stats.total_lessons, icon: BookOpen, color: 'text-amber-400' },
    { label: 'محاولات الاختبار', value: stats.total_score_entries, icon: Target, color: 'text-red-400' },
    { label: 'جلسات المحادثة', value: stats.total_chat_sessions, icon: MessageSquare, color: 'text-indigo-400' },
  ];

  const mgmtCards = [
    { label: 'المديريات', value: stats.total_directorates || 0, icon: Building2, color: 'text-purple-400' },
    { label: 'المدارس', value: stats.total_schools || 0, icon: School, color: 'text-indigo-400' },
    { label: 'المدراء', value: stats.total_directors || 0, icon: Crown, color: 'text-amber-400' },
    { label: 'مدراء المدارس', value: stats.total_principals || 0, icon: ShieldCheck, color: 'text-emerald-400' },
    { label: 'المعلمون', value: stats.total_teachers || 0, icon: UserCheck, color: 'text-[#4da6ff]' },
    { label: 'أولياء الأمور', value: stats.total_parents || 0, icon: Users, color: 'text-pink-400' },
  ];

  const dirDist = stats.directorate_distribution || [];
  const schDist = stats.school_distribution || [];
  const rolePie = [
    { name: 'طلاب', value: stats.total_students || 0, color: '#3b82f6' },
    { name: 'معلمون', value: stats.total_teachers || 0, color: '#10b981' },
    { name: 'مدراء مدارس', value: stats.total_principals || 0, color: '#f59e0b' },
    { name: 'مدراء', value: stats.total_directors || 0, color: '#8b5cf6' },
  ].filter(x => x.value > 0);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(c => (
          <Card key={c.label} className="bg-white/5 border-white/10">
            <CardContent className="p-4 text-center">
              <c.icon className={`w-5 h-5 mx-auto mb-2 ${c.color}`} />
              <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
              <p className="text-white/40 text-xs mt-1">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Management hierarchy counts */}
      <div>
        <h3 className="text-sm font-bold text-white mb-3">الهيكل الإداري</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-mgmt-cards">
          {mgmtCards.map(c => (
            <Card key={c.label} className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <c.icon className={`w-5 h-5 mx-auto mb-2 ${c.color}`} />
                <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
                <p className="text-white/40 text-xs mt-1">{c.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Directorate distribution chart */}
        {dirDist.length > 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-purple-400" /> توزيع المديريات</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={dirDist}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                  <XAxis dataKey="name" tick={{ fill: '#ffffffa0', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#ffffff60', fontSize: 11 }} />
                  <Tooltip contentStyle={TT} />
                  <Legend wrapperStyle={{ color: '#ffffffa0', fontSize: 11 }} />
                  <Bar dataKey="schools" name="مدارس" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="teachers" name="معلمون" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="students" name="طلاب" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Role distribution pie */}
        {rolePie.length > 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#4da6ff]" /> توزيع المستخدمين حسب الدور</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={rolePie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={85} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: '#ffffff40' }}>
                    {rolePie.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={TT} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Top schools by students */}
        {schDist.length > 0 && (
          <Card className="bg-white/5 border-white/10 lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><School className="w-4 h-4 text-indigo-400" /> أكبر المدارس (طلاب ومعلمون)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={schDist}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                  <XAxis dataKey="name" tick={{ fill: '#ffffffa0', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#ffffff60', fontSize: 11 }} />
                  <Tooltip contentStyle={TT} />
                  <Legend wrapperStyle={{ color: '#ffffffa0', fontSize: 11 }} />
                  <Bar dataKey="students" name="طلاب" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="teachers" name="معلمون" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {Object.keys(stats.grade_distribution).length > 0 && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-white mb-3">توزيع الطلاب على الصفوف</h3>
            <div className="grid grid-cols-3 sm:grid-cols-9 gap-2">
              {[1,2,3,4,5,6,7,8,9].map(g => (
                <div key={g} className="text-center rounded-lg bg-white/5 p-2">
                  <p className="text-lg font-bold text-[#4da6ff]">{stats.grade_distribution[String(g)] || 0}</p>
                  <p className="text-[10px] text-white/30">صف {g}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Students Tab ───
function StudentsTab({ headers }) {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', name: '', grade_number: '', school_name: '', teacher_name: '', school_id: '', teacher_ids: [] });
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const fetchStudents = useCallback(() => {
    axios.get(`${API_URL}/api/admin/students`, { headers }).then(r => setStudents(r.data)).catch(() => {});
  }, [headers]);

  useEffect(() => {
    fetchStudents();
    axios.get(`${API_URL}/api/admin/schools`, { headers }).then(r => setSchools(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/teachers`, { headers }).then(r => setTeachers(r.data)).catch(() => {});
  }, [headers, fetchStudents]);

  const toggleTeacher = (tid) => {
    setForm(f => ({ ...f, teacher_ids: f.teacher_ids.includes(tid) ? f.teacher_ids.filter(x => x !== tid) : [...f.teacher_ids, tid] }));
  };

  const filtered = students.filter(s =>
    s.username?.includes(search) || s.name?.includes(search)
  );

  const handleAdd = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/students`, {
        ...form,
        grade_number: form.grade_number ? Number(form.grade_number) : null
      }, { headers });
      toast.success('تم إضافة الطالب');
      setShowAdd(false);
      setForm({ username: '', password: '', name: '', grade_number: '', school_name: '', teacher_name: '', school_id: '', teacher_ids: [] });
      fetchStudents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'خطأ');
    }
  };

  const handleUpdate = async (userId) => {
    try {
      const payload = { name: form.name, grade_number: form.grade_number ? Number(form.grade_number) : null, school_name: form.school_name, teacher_name: form.teacher_name, school_id: form.school_id, teacher_ids: form.teacher_ids };
      if (form.password) payload.password = form.password;
      await axios.put(`${API_URL}/api/admin/students/${userId}`, payload, { headers });
      toast.success('تم التحديث');
      setEditId(null);
      fetchStudents();
    } catch {
      toast.error('خطأ');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`هل تريد حذف ${username}؟`)) return;
    await axios.delete(`${API_URL}/api/admin/students/${userId}`, { headers });
    toast.success('تم الحذف');
    fetchStudents();
  };

  return (
    <div className="space-y-4" data-testid="admin-students">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="بحث عن طالب..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl pr-10 pl-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#4da6ff]"
            data-testid="search-students"
          />
        </div>
        <Button onClick={() => { setShowAdd(true); setForm({ username: '', password: '', name: '', grade_number: '', school_name: '', teacher_name: '', school_id: '', teacher_id: '' }); }} className="bg-[#4da6ff] hover:bg-[#3d96ef] shrink-0" data-testid="add-student-btn">
          <Plus className="w-4 h-4 ml-1" />
          إضافة طالب
        </Button>
      </div>

      {/* Add Form */}
      {showAdd && (
        <Card className="bg-white/5 border-[#4da6ff]/30">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-white text-sm">إضافة طالب جديد</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <input placeholder="اسم المستخدم" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="add-username" />
              <input placeholder="كلمة المرور" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="add-password" />
              <input placeholder="الاسم الكامل" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="add-name" />
              <select value={form.grade_number} onChange={e => setForm(f => ({ ...f, grade_number: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="add-grade">
                <option value="">اختر الصف</option>
                {[1,2,3,4,5,6,7,8,9].map(g => <option key={g} value={g}>الصف {g}</option>)}
              </select>
              <select value={form.school_id} onChange={e => { const sc = schools.find(s => s.id === e.target.value); setForm(f => ({ ...f, school_id: e.target.value, school_name: sc?.name || '' })); }} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="add-school-id">
                <option value="">اختر المدرسة</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <p className="text-white/50 text-xs mb-1.5">اختر المعلمين:</p>
              <div className="flex flex-wrap gap-1.5">
                {teachers.map(t => (
                  <button key={t.id} type="button" onClick={() => toggleTeacher(t.id)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all border ${
                      form.teacher_ids.includes(t.id) ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                    }`}>{t.name}{t.school_name ? ` (${t.school_name})` : ''}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} className="bg-emerald-500 hover:bg-emerald-600" data-testid="confirm-add-btn"><Check className="w-3 h-3 ml-1" />إضافة</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Students List */}
      <div className="text-white/30 text-xs">{filtered.length} طالب</div>
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {filtered.slice(0, 50).map(s => (
          <Card key={s.id} className="bg-white/5 border-white/10">
            <CardContent className="p-3">
              {editId === s.id ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="الاسم" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                    <input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="كلمة مرور جديدة (اختياري)" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                    <select value={form.grade_number} onChange={e => setForm(f => ({ ...f, grade_number: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none">
                      <option value="">بدون صف</option>
                      {[1,2,3,4,5,6,7,8,9].map(g => <option key={g} value={g}>الصف {g}</option>)}
                    </select>
                    <select value={form.school_id || ''} onChange={e => { const sc = schools.find(x => x.id === e.target.value); setForm(f => ({ ...f, school_id: e.target.value, school_name: sc?.name || '' })); }} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none">
                      <option value="">اختر المدرسة</option>
                      {schools.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                    </select>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => handleUpdate(s.id)} className="bg-emerald-500 text-xs"><Check className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="text-white/40 text-xs"><X className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-white/50 text-[10px] mb-1">المعلمون:</p>
                    <div className="flex flex-wrap gap-1">
                      {teachers.map(tc => (
                        <button key={tc.id} type="button" onClick={() => toggleTeacher(tc.id)}
                          className={`px-2 py-0.5 rounded-lg text-[10px] font-medium transition-all border ${
                            form.teacher_ids.includes(tc.id) ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-white/10 text-white/30 hover:bg-white/10'
                          }`}>{tc.name}</button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-[#4da6ff]/20 flex items-center justify-center shrink-0">
                      <span className="text-[10px] text-[#4da6ff] font-bold">{s.grade_number || '-'}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{s.name}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white/30 text-xs" dir="ltr">{s.username}</p>
                        {s.school_name && <span className="text-[#4da6ff]/60 text-[10px]">{s.school_name}</span>}
                        {s.teacher_ids?.length > 0
                          ? teachers.filter(t => s.teacher_ids.includes(t.id)).map(t => (
                              <span key={t.id} className="text-amber-400/60 text-[10px]">{t.name}</span>
                            ))
                          : s.teacher_name && <span className="text-amber-400/60 text-[10px]">{s.teacher_name}</span>
                        }
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a href={`/admin/student-profile/${s.id}`} className="p-1.5 rounded-lg hover:bg-[#4da6ff]/20 text-white/30 hover:text-[#4da6ff] transition-colors" title="عرض الملف الشخصي">
                      <BarChart3 className="w-3.5 h-3.5" />
                    </a>
                    <button onClick={() => { setEditId(s.id); setForm({ name: s.name || '', password: '', grade_number: s.grade_number || '', school_name: s.school_name || '', teacher_name: s.teacher_name || '', school_id: s.school_id || '', teacher_ids: s.teacher_ids || (s.teacher_id ? [s.teacher_id] : []) }); }} className="p-1.5 rounded-lg hover:bg-white/10 text-white/30 hover:text-white transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(s.id, s.username)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}


// ─── Subjects Tab ───
function SubjectsTab({ headers }) {
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name_ar: '', name_en: '', grade_id: '', semester: '1' });
  const [filterGrade, setFilterGrade] = useState('');

  const fetchData = useCallback(() => {
    axios.get(`${API_URL}/api/admin/subjects`, { headers }).then(r => setSubjects(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/grades`, { headers }).then(r => setGrades(r.data)).catch(() => {});
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gradeMap = {};
  grades.forEach(g => { gradeMap[g.id] = g; });

  const filtered = filterGrade ? subjects.filter(s => s.grade_id === filterGrade) : subjects;

  const handleAdd = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/subjects`, form, { headers });
      toast.success('تم إضافة المادة');
      setShowAdd(false);
      setForm({ name_ar: '', name_en: '', grade_id: '', semester: '1' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'خطأ');
    }
  };

  const handleDelete = async (subjectId, name) => {
    if (!window.confirm(`هل تريد حذف مادة "${name}"؟ سيتم حذف جميع دروسها أيضاً.`)) return;
    await axios.delete(`${API_URL}/api/admin/subjects/${subjectId}`, { headers });
    toast.success('تم حذف المادة ودروسها');
    fetchData();
  };

  return (
    <div className="space-y-4" data-testid="admin-subjects">
      <div className="flex items-center gap-3">
        <select value={filterGrade} onChange={e => setFilterGrade(e.target.value)} className="bg-[#1a1a2e] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none">
          <option value="">كل الصفوف</option>
          {grades.map(g => <option key={g.id} value={g.id}>{g.name_ar}</option>)}
        </select>
        <div className="flex-1" />
        <Button onClick={() => { setShowAdd(true); setForm({ name_ar: '', name_en: '', grade_id: '', semester: '1' }); }} className="bg-[#4da6ff] hover:bg-[#3d96ef] shrink-0" data-testid="add-subject-btn">
          <Plus className="w-4 h-4 ml-1" />
          إضافة مادة
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-white/5 border-[#4da6ff]/30">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-white text-sm">إضافة مادة جديدة</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <input placeholder="اسم المادة بالعربية" value={form.name_ar} onChange={e => setForm(f => ({ ...f, name_ar: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
              <input placeholder="اسم المادة بالإنجليزية" value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
              <select value={form.grade_id} onChange={e => setForm(f => ({ ...f, grade_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                <option value="">اختر الصف</option>
                {grades.map(g => <option key={g.id} value={g.id}>{g.name_ar}</option>)}
              </select>
              <select value={form.semester} onChange={e => setForm(f => ({ ...f, semester: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                <option value="1">الفصل الأول</option>
                <option value="2">الفصل الثاني</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} className="bg-emerald-500 hover:bg-emerald-600"><Check className="w-3 h-3 ml-1" />إضافة</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-white/30 text-xs">{filtered.length} مادة</div>
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {filtered.map(s => (
          <Card key={s.id} className="bg-white/5 border-white/10">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{s.name_ar}</p>
                  <p className="text-white/30 text-xs">{gradeMap[s.grade_id]?.name_ar} — الفصل {s.semester || 1}</p>
                </div>
                <button onClick={() => handleDelete(s.id, s.name_ar)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Lessons Tab ───
function LessonsTab({ headers }) {
  const [lessons, setLessons] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [filterGrade, setFilterGrade] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [search, setSearch] = useState('');
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ title_ar: '', title_en: '', grade_id: '', subject_id: '' });

  const fetchLessons = useCallback(() => {
    axios.get(`${API_URL}/api/admin/lessons`, { headers }).then(r => setLessons(r.data)).catch(() => {});
  }, [headers]);

  useEffect(() => {
    fetchLessons();
    axios.get(`${API_URL}/api/admin/subjects`, { headers }).then(r => setSubjects(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/grades`, { headers }).then(r => setGrades(r.data)).catch(() => {});
  }, [headers, fetchLessons]);

  const gradeMap = {};
  grades.forEach(g => { gradeMap[g.id] = g; });
  const subjectMap = {};
  subjects.forEach(s => { subjectMap[s.id] = s; });

  const filtered = lessons.filter(l => {
    if (filterGrade && l.grade_id !== filterGrade) return false;
    if (filterSubject && l.subject_id !== filterSubject) return false;
    if (search && !l.title_ar?.includes(search) && !l.title_en?.includes(search)) return false;
    return true;
  });

  const filteredSubjects = filterGrade
    ? subjects.filter(s => s.grade_id === filterGrade)
    : subjects;

  const handlePdfUpload = async (lessonId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/api/admin/lessons/${lessonId}/upload-pdf`, formData, {
        headers
      });
      toast.success('تم رفع ملف PDF');
    } catch {
      toast.error('خطأ في رفع الملف');
    }
  };

  const handleAddLesson = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/lessons`, addForm, { headers });
      toast.success('تم إضافة الدرس');
      setShowAdd(false);
      setAddForm({ title_ar: '', title_en: '', grade_id: '', subject_id: '' });
      fetchLessons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'خطأ');
    }
  };

  const handleDeleteLesson = async (lessonId, title) => {
    if (!window.confirm(`هل تريد حذف "${title}"؟`)) return;
    await axios.delete(`${API_URL}/api/admin/lessons/${lessonId}`, { headers });
    toast.success('تم حذف الدرس');
    fetchLessons();
  };

  const addFormSubjects = addForm.grade_id ? subjects.filter(s => s.grade_id === addForm.grade_id) : [];

  return (
    <div className="space-y-4" data-testid="admin-lessons">
      <div className="flex flex-wrap items-center gap-3">
        <select value={filterGrade} onChange={e => { setFilterGrade(e.target.value); setFilterSubject(''); }} className="bg-[#1a1a2e] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none" data-testid="filter-grade">
          <option value="" className="bg-[#1a1a2e] text-white">كل الصفوف</option>
          {grades.map(g => <option key={g.id} value={g.id} className="bg-[#1a1a2e] text-white">{g.name_ar}</option>)}
        </select>
        <select value={filterSubject} onChange={e => setFilterSubject(e.target.value)} className="bg-[#1a1a2e] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none" data-testid="filter-subject">
          <option value="" className="bg-[#1a1a2e] text-white">كل المواد</option>
          {filteredSubjects.map(s => <option key={s.id} value={s.id} className="bg-[#1a1a2e] text-white">{s.name_ar}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input type="text" placeholder="بحث عن درس..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl pr-10 pl-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#4da6ff]"
          />
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-[#4da6ff] hover:bg-[#3d96ef] shrink-0" data-testid="add-lesson-btn">
          <Plus className="w-4 h-4 ml-1" />
          إضافة درس
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-white/5 border-[#4da6ff]/30">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-white text-sm">إضافة درس جديد</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <input placeholder="اسم الدرس بالعربية" value={addForm.title_ar} onChange={e => setAddForm(f => ({ ...f, title_ar: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
              <select value={addForm.grade_id} onChange={e => setAddForm(f => ({ ...f, grade_id: e.target.value, subject_id: '' }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                <option value="">اختر الصف</option>
                {grades.map(g => <option key={g.id} value={g.id}>{g.name_ar}</option>)}
              </select>
              <select value={addForm.subject_id} onChange={e => setAddForm(f => ({ ...f, subject_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                <option value="">اختر المادة</option>
                {addFormSubjects.map(s => <option key={s.id} value={s.id}>{s.name_ar}</option>)}
              </select>
              <input placeholder="اسم الدرس بالإنجليزية (اختياري)" value={addForm.title_en} onChange={e => setAddForm(f => ({ ...f, title_en: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddLesson} className="bg-emerald-500 hover:bg-emerald-600"><Check className="w-3 h-3 ml-1" />إضافة</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-white/30 text-xs">{filtered.length} درس</div>

      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {filtered.slice(0, 50).map(l => {
          const subj = subjectMap[l.subject_id];
          const grade = gradeMap[l.grade_id];
          const isExpanded = expandedLesson === l.id;

          return (
            <Card key={l.id} className="bg-white/5 border-white/10">
              <CardContent className="p-3">
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedLesson(isExpanded ? null : l.id)}>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">{l.title_ar}</p>
                    <p className="text-white/30 text-xs">{grade?.name_ar} - {subj?.name_ar}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {l.pdf_url && <FileText className="w-3.5 h-3.5 text-emerald-400" title="PDF" />}
                    {l.video_url && <Video className="w-3.5 h-3.5 text-red-400" title="فيديو" />}
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteLesson(l.id, l.title_ar); }} className="p-1 rounded hover:bg-red-500/20 text-white/20 hover:text-red-400 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-3" onClick={e => e.stopPropagation()}>
                    {/* PDF Upload */}
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs cursor-pointer hover:bg-emerald-500/20 transition-colors">
                        <FileText className="w-3.5 h-3.5" />
                        {l.pdf_url ? 'تغيير PDF' : 'رفع PDF'}
                        <input type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) handlePdfUpload(l.id, e.target.files[0]); }} />
                      </label>
                      {l.pdf_url && <span className="text-emerald-400/50 text-xs">PDF موجود</span>}
                    </div>

                    {/* Video Upload */}
                    <VideoUploader lessonId={l.id} hasVideo={!!l.video_url} headers={headers} onUploaded={(url) => {
                      setLessons(prev => prev.map(ll => ll.id === l.id ? { ...ll, video_url: url } : ll));
                    }} />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function VideoUploader({ lessonId, hasVideo, headers, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/admin/lessons/${lessonId}/upload-video`, formData, {
        headers,
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); }
      });
      toast.success('تم رفع الفيديو بنجاح');
      onUploaded(res.data.video_url);
    } catch {
      toast.error('خطأ في رفع الفيديو');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition-colors ${
        uploading ? 'bg-white/5 border-white/10 text-white/30 cursor-wait' :
        'bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20'
      }`}>
        <Video className="w-3.5 h-3.5" />
        {uploading ? `جارٍ الرفع ${progress}%` : hasVideo ? 'تغيير الفيديو' : 'رفع فيديو'}
        <input type="file" accept="video/*" className="hidden" onChange={handleUpload} disabled={uploading} />
      </label>
      {hasVideo && !uploading && <span className="text-red-400/50 text-xs">فيديو موجود</span>}
    </div>
  );
}


// ─── Olympics Tab ───
function OlympicsTab({ headers }) {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [view, setView] = useState('manage');

  const fetchSubjects = () => {
    axios.get(`${API_URL}/api/admin/olympics/subjects`, { headers })
      .then(r => setSubjects(r.data))
      .catch(() => toast.error('فشل في تحميل المواد'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSubjects(); }, [headers]);

  const handleGenerate = async (subjectId, subjectName) => {
    if (!window.confirm(`هل تريد توليد 100 سؤال أولمبياد لمادة "${subjectName}"؟\nهذا سيستبدل الأسئلة السابقة وقد يستغرق دقيقة.`)) return;
    setGenerating(subjectId);
    try {
      const res = await axios.post(`${API_URL}/api/admin/olympics/generate/${subjectId}`, {}, { headers, timeout: 300000 });
      toast.success(`تم توليد ${res.data.count} سؤال بنجاح`);
      fetchSubjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في توليد الأسئلة');
    } finally {
      setGenerating(null);
    }
  };

  // Group by grade_number
  const byGrade = {};
  subjects.forEach(s => {
    const gn = s.grade_number || 99;
    if (!byGrade[gn]) byGrade[gn] = { name: s.grade_name || `صف ${gn}`, subjects: [] };
    byGrade[gn].subjects.push(s);
  });
  const sortedGrades = Object.entries(byGrade).sort((a, b) => Number(a[0]) - Number(b[0]));

  if (loading) return <div className="text-white/30 text-center py-12">جارٍ التحميل...</div>;

  const totalGenerated = subjects.filter(s => s.olympics_count > 0).length;

  return (
    <div className="space-y-4" data-testid="admin-olympics-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-amber-400 flex items-center gap-2">
          <Trophy className="w-5 h-5" /> الأولمبياد
        </h2>
        <div className="flex items-center gap-2 bg-white/5 p-1 rounded-xl border border-white/10">
          <button
            onClick={() => setView('manage')}
            data-testid="olympics-view-manage"
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${view === 'manage' ? 'bg-amber-500/20 text-amber-400' : 'text-white/50 hover:text-white/80'}`}
          >إدارة الأسئلة</button>
          <button
            onClick={() => setView('report')}
            data-testid="olympics-view-report"
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${view === 'report' ? 'bg-[#4da6ff]/20 text-[#4da6ff]' : 'text-white/50 hover:text-white/80'}`}
          >تقرير المشاركة</button>
        </div>
      </div>

      {view === 'report' ? (
        <OlympicsReportView headers={headers} />
      ) : (
        <>
          <div className="flex items-center justify-end">
            <span className="text-white/40 text-xs">{totalGenerated} من {subjects.length} مادة جاهزة</span>
          </div>
          <p className="text-white/40 text-xs">اختر المادة لتوليد 100 سؤال من محتوى دروس الـ PDF. التوليد يستغرق حوالي دقيقة لكل مادة.</p>

          {sortedGrades.map(([gn, group]) => (
            <Card key={gn} className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <h3 className="text-sm font-bold text-[#4da6ff] mb-3 pb-2 border-b border-white/10">
                  {group.name}
                </h3>
                <div className="space-y-2">
                  {group.subjects.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${s.olympics_count > 0 ? 'bg-emerald-500' : 'bg-white/20'}`} />
                        <div>
                          <p className="text-white text-sm font-medium">{s.name_ar}</p>
                          <p className="text-white/30 text-xs">الفصل {s.semester || 2}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {s.olympics_count > 0 && (
                          <span className="text-emerald-400 text-xs bg-emerald-500/10 px-2 py-1 rounded-lg border border-emerald-500/20">
                            {s.olympics_count} سؤال
                          </span>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleGenerate(s.id, `${s.name_ar} - ${group.name}`)}
                          disabled={generating !== null}
                          className={`text-xs ${generating === s.id
                            ? 'bg-amber-500/20 text-amber-400'
                            : s.olympics_count > 0
                              ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20'
                              : 'bg-[#4da6ff]/10 text-[#4da6ff] hover:bg-[#4da6ff]/20 border border-[#4da6ff]/20'
                          }`}
                          data-testid={`generate-olympics-${s.id}`}
                        >
                          {generating === s.id ? (
                            <><Loader2 className="w-3 h-3 ml-1 animate-spin" /> جارٍ التوليد...</>
                          ) : s.olympics_count > 0 ? (
                            <><Trophy className="w-3 h-3 ml-1" /> إعادة التوليد</>
                          ) : (
                            <><Plus className="w-3 h-3 ml-1" /> توليد الأسئلة</>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

// ─── Olympics Participation Report ───
function OlympicsReportView({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSubj, setOpenSubj] = useState({});

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/olympics/report`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error('فشل في تحميل التقرير'))
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="text-white/30 text-center py-12">جارٍ تحميل التقرير...</div>;
  if (!data || data.grades.length === 0) return <div className="text-white/30 text-center py-12" data-testid="olympics-report-empty">لا توجد مشاركات أولمبياد بعد.</div>;

  const pctColor = (p) => p >= 80 ? 'text-emerald-400' : p >= 50 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-4" data-testid="olympics-report">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card className="bg-white/5 border-white/10"><CardContent className="p-4 text-center"><Trophy className="w-5 h-5 mx-auto mb-2 text-amber-400" /><p className="text-2xl font-bold text-amber-400">{data.total_subjects}</p><p className="text-white/40 text-xs mt-1">مواد بها أولمبياد</p></CardContent></Card>
        <Card className="bg-white/5 border-white/10"><CardContent className="p-4 text-center"><Users className="w-5 h-5 mx-auto mb-2 text-[#4da6ff]" /><p className="text-2xl font-bold text-[#4da6ff]">{data.total_participants}</p><p className="text-white/40 text-xs mt-1">إجمالي المشاركين</p></CardContent></Card>
        <Card className="bg-white/5 border-white/10"><CardContent className="p-4 text-center"><GraduationCap className="w-5 h-5 mx-auto mb-2 text-purple-400" /><p className="text-2xl font-bold text-purple-400">{data.grades.length}</p><p className="text-white/40 text-xs mt-1">صفوف مشاركة</p></CardContent></Card>
      </div>

      {data.grades.map(g => (
        <Card key={g.grade_number} className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-[#4da6ff] mb-3 pb-2 border-b border-white/10">{g.grade_name}</h3>
            <div className="space-y-2">
              {g.subjects.map(s => {
                const key = s.subject_id;
                const open = openSubj[key];
                return (
                  <div key={key} className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
                    <button
                      onClick={() => setOpenSubj(o => ({ ...o, [key]: !o[key] }))}
                      data-testid={`olympics-report-subject-${key}`}
                      className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-right">
                        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
                        <div>
                          <p className="text-white text-sm font-medium">{s.subject_name}</p>
                          <p className="text-white/30 text-xs">الفصل {s.semester} · {s.total_questions} سؤال</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-[#4da6ff] bg-[#4da6ff]/10 px-2 py-1 rounded-lg">{s.participants_count} مشارك</span>
                        <span className={`${pctColor(s.avg_percentage)} bg-white/5 px-2 py-1 rounded-lg`}>متوسط {s.avg_percentage}%</span>
                      </div>
                    </button>
                    {open && (
                      <div className="px-3 pb-3">
                        {s.students.length === 0 ? (
                          <p className="text-white/30 text-xs text-center py-3">لا يوجد مشاركون في هذه المادة.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid={`olympics-report-table-${key}`}>
                              <thead>
                                <tr className="text-white/40 border-b border-white/10">
                                  <th className="text-right py-2 px-2 font-medium">#</th>
                                  <th className="text-right py-2 px-2 font-medium">الطالب</th>
                                  <th className="text-right py-2 px-2 font-medium">المدرسة</th>
                                  <th className="text-right py-2 px-2 font-medium">المديرية</th>
                                  <th className="text-right py-2 px-2 font-medium">مدير المديرية</th>
                                  <th className="text-center py-2 px-2 font-medium">صحيح</th>
                                  <th className="text-center py-2 px-2 font-medium">النسبة</th>
                                </tr>
                              </thead>
                              <tbody>
                                {s.students.map((st, i) => (
                                  <tr key={st.user_id} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="py-2 px-2 text-white/30">{i + 1}</td>
                                    <td className="py-2 px-2 text-white">{st.name}</td>
                                    <td className="py-2 px-2 text-white/60">{st.school_name}</td>
                                    <td className="py-2 px-2 text-purple-400">{st.directorate_name}</td>
                                    <td className="py-2 px-2 text-amber-400">{st.director_name}</td>
                                    <td className="py-2 px-2 text-center text-white/60">{st.correct}/{st.total_questions}</td>
                                    <td className={`py-2 px-2 text-center font-bold ${pctColor(st.percentage)}`}>{st.percentage}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


// ─── Directorates Tab ───
function DirectoratesTab({ headers }) {
  const [directorates, setDirectorates] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '' });

  const fetchData = useCallback(() => {
    axios.get(`${API_URL}/api/admin/directorates`, { headers }).then(r => setDirectorates(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    await axios.post(`${API_URL}/api/admin/directorates`, form, { headers });
    toast.success('تمت إضافة المديرية');
    setShowAdd(false); setForm({ name: '', address: '', phone: '' }); fetchData();
  };
  const handleUpdate = async (id) => {
    await axios.put(`${API_URL}/api/admin/directorates/${id}`, form, { headers });
    toast.success('تم التحديث'); setEditId(null); fetchData();
  };
  const handleDelete = async (id, name) => {
    if (!window.confirm(`حذف "${name}"؟`)) return;
    await axios.delete(`${API_URL}/api/admin/directorates/${id}`, { headers });
    toast.success('تم الحذف'); fetchData();
  };

  return (
    <div className="space-y-4" data-testid="admin-directorates">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-purple-400 flex items-center gap-2"><Building2 className="w-5 h-5" /> المديريات</h2>
        <Button onClick={() => { setShowAdd(true); setForm({ name: '', address: '', phone: '' }); }} className="bg-purple-500 hover:bg-purple-600" data-testid="add-directorate-btn">
          <Plus className="w-4 h-4 ml-1" /> إضافة مديرية
        </Button>
      </div>
      {showAdd && (
        <Card className="bg-white/5 border-purple-500/30">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-white text-sm">إضافة مديرية جديدة</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input placeholder="اسم المديرية" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="directorate-name" />
              <input placeholder="العنوان" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
              <input placeholder="رقم الهاتف" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} className="bg-emerald-500"><Check className="w-3 h-3 ml-1" />إضافة</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="space-y-2">
        {directorates.map(d => (
          <Card key={d.id} className="bg-white/5 border-white/10">
            <CardContent className="p-3">
              {editId === d.id ? (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                  <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="العنوان" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="الهاتف" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleUpdate(d.id)} className="bg-emerald-500 text-xs"><Check className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="text-white/40"><X className="w-3 h-3" /></Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0"><Building2 className="w-4 h-4 text-purple-400" /></div>
                    <div>
                      <p className="text-white text-sm font-medium">{d.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-white/30">
                        {d.address && <span>{d.address}</span>}
                        {d.phone && <span>{d.phone}</span>}
                        <span className="text-purple-400">{d.school_count} مدرسة</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditId(d.id); setForm({ name: d.name, address: d.address || '', phone: d.phone || '' }); }} className="p-1.5 rounded-lg hover:bg-white/10 text-white/30 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(d.id, d.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {directorates.length === 0 && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لا توجد مديريات</div>}
      </div>
    </div>
  );
}


// ─── Ministers Tab ───
function MinistersTab({ headers }) {
  const [ministers, setMinisters] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', name: '', phone: '' });
  const fetchAll = useCallback(() => { axios.get(`${API_URL}/api/admin/ministers`, { headers }).then(r => setMinisters(r.data)).catch(() => {}); }, [headers]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  const resetForm = () => { setForm({ username: '', password: '', name: '', phone: '' }); setShowAdd(false); setEditId(null); };
  const handleAdd = async () => {
    if (!form.username.trim() || !form.password.trim() || !form.name.trim()) { toast.error('جميع الحقول مطلوبة'); return; }
    try { await axios.post(`${API_URL}/api/admin/ministers`, form, { headers }); toast.success('تمت الإضافة'); resetForm(); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'خطأ'); }
  };
  const openEdit = (m) => { setEditId(m.id); setShowAdd(false); setForm({ username: m.username || '', password: '', name: m.name || '', phone: m.phone || '' }); };
  const handleUpdate = async () => {
    if (!form.name.trim() || !form.username.trim()) { toast.error('الاسم واسم المستخدم مطلوبان'); return; }
    const payload = { name: form.name, username: form.username, phone: form.phone };
    if (form.password.trim()) payload.password = form.password;
    try { await axios.put(`${API_URL}/api/admin/ministers/${editId}`, payload, { headers }); toast.success('تم التحديث'); resetForm(); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'خطأ'); }
  };
  const handleDelete = async (id, name) => { if (!window.confirm(`حذف "${name}"؟`)) return; await axios.delete(`${API_URL}/api/admin/ministers/${id}`, { headers }); toast.success('تم الحذف'); fetchAll(); };
  return (
    <div className="space-y-4" data-testid="admin-ministers">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-yellow-400 flex items-center gap-2"><Star className="w-5 h-5" /> الوزراء</h2>
        <Button onClick={() => { setShowAdd(true); setEditId(null); setForm({ username: '', password: '', name: '', phone: '' }); }} className="bg-yellow-500 hover:bg-yellow-600 text-black" data-testid="add-minister-btn"><Plus className="w-4 h-4 ml-1" /> إضافة وزير</Button>
      </div>
      {showAdd && (
        <Card className="bg-white/5 border-yellow-500/30"><CardContent className="p-4 space-y-3">
          <h3 className="font-bold text-white text-sm">إضافة وزير جديد</h3>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="اسم المستخدم" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="minister-username" />
            <input placeholder="كلمة المرور" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
            <input placeholder="الاسم" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="minister-name" />
            <input placeholder="الهاتف (اختياري)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
          </div>
          <div className="flex gap-2"><Button size="sm" onClick={handleAdd} className="bg-emerald-500"><Check className="w-3 h-3 ml-1" />إضافة</Button><Button size="sm" variant="ghost" onClick={resetForm} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button></div>
        </CardContent></Card>
      )}
      <div className="space-y-2">
        {ministers.map(m => (
          editId === m.id ? (
            <Card key={m.id} className="bg-white/5 border-yellow-500/30" data-testid={`minister-edit-${m.id}`}><CardContent className="p-4 space-y-3">
              <h3 className="font-bold text-white text-sm">تعديل بيانات الوزير</h3>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="اسم المستخدم" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="minister-edit-username" />
                <input placeholder="كلمة مرور جديدة (اتركها فارغة للإبقاء)" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="minister-edit-password" />
                <input placeholder="الاسم" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="minister-edit-name" />
                <input placeholder="الهاتف (اختياري)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
              </div>
              <div className="flex gap-2"><Button size="sm" onClick={handleUpdate} className="bg-emerald-500" data-testid="minister-save-btn"><Check className="w-3 h-3 ml-1" />حفظ</Button><Button size="sm" variant="ghost" onClick={resetForm} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button></div>
            </CardContent></Card>
          ) : (
            <Card key={m.id} className="bg-white/5 border-white/10"><CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0"><Star className="w-4 h-4 text-yellow-400" /></div><div><p className="text-white text-sm font-medium">{m.name}</p><p className="text-white/30 text-[10px]">{m.username}{m.phone ? ` - ${m.phone}` : ''}</p></div></div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(m)} className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white" data-testid={`minister-edit-btn-${m.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => handleDelete(m.id, m.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </CardContent></Card>
          )
        ))}
        {ministers.length === 0 && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لا يوجد وزراء</div>}
      </div>
    </div>
  );
}

// ─── Directors Tab ───
function DirectorsTab({ headers }) {
  const [directors, setDirectors] = useState([]);
  const [directorates, setDirectorates] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', name: '', directorate_id: '', phone: '' });

  const fetchAll = useCallback(() => {
    axios.get(`${API_URL}/api/admin/directors`, { headers }).then(r => setDirectors(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/directorates`, { headers }).then(r => setDirectorates(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAdd = async () => {
    if (!form.username.trim() || !form.password.trim() || !form.name.trim()) { toast.error('جميع الحقول مطلوبة'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/directors`, form, { headers });
      toast.success('تمت إضافة مدير المديرية');
      setShowAdd(false); setForm({ username: '', password: '', name: '', directorate_id: '', phone: '' }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'خطأ'); }
  };
  const handleUpdate = async (id) => {
    const payload = { name: form.name, directorate_id: form.directorate_id, phone: form.phone };
    if (form.password.trim()) payload.password = form.password.trim();
    try {
      await axios.put(`${API_URL}/api/admin/directors/${id}`, payload, { headers });
      toast.success('تم التحديث'); setEditId(null); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'تعذر حفظ التعديلات'); }
  };
  const handleDelete = async (id, name) => {
    if (!window.confirm(`حذف "${name}"؟`)) return;
    await axios.delete(`${API_URL}/api/admin/directors/${id}`, { headers });
    toast.success('تم الحذف'); fetchAll();
  };

  return (
    <div className="space-y-4" data-testid="admin-directors">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-amber-400 flex items-center gap-2"><Crown className="w-5 h-5" /> مديرو المديريات</h2>
        <Button onClick={() => { setShowAdd(true); setForm({ username: '', password: '', name: '', directorate_id: '', phone: '' }); }} className="bg-amber-500 hover:bg-amber-600" data-testid="add-director-btn">
          <Plus className="w-4 h-4 ml-1" /> إضافة مدير مديرية
        </Button>
      </div>
      {showAdd && (
        <Card className="bg-white/5 border-amber-500/30">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-white text-sm">إضافة مدير مديرية جديد</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <input placeholder="رقم المدير الفريد" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="director-username" />
              <input type="password" autoComplete="new-password" placeholder="كلمة المرور" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="director-password" />
              <input placeholder="الاسم" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="director-name" />
              <select value={form.directorate_id} onChange={e => setForm(f => ({ ...f, directorate_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="director-directorate">
                <option value="">اختر المديرية</option>
                {directorates.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <input placeholder="رقم الهاتف (اختياري)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} className="bg-emerald-500"><Check className="w-3 h-3 ml-1" />إضافة</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="space-y-2">
        {directors.map(d => (
          <Card key={d.id} className="bg-white/5 border-white/10" data-testid={`director-account-${d.id}`}>
            <CardContent className="p-3">
              {editId === d.id ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="الاسم" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" data-testid={`director-name-edit-${d.id}`} />
                  <PasswordChangeField key={d.id} value={form.password} onChange={password => setForm(f => ({ ...f, password }))} testId={`director-password-${d.id}`} />
                  <select value={form.directorate_id} onChange={e => setForm(f => ({ ...f, directorate_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none">
                    <option value="">اختر المديرية</option>
                    {directorates.map(dir => <option key={dir.id} value={dir.id}>{dir.name}</option>)}
                  </select>
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleUpdate(d.id)} className="bg-emerald-500 text-xs" aria-label="حفظ التعديلات" data-testid={`director-save-${d.id}`}><Check className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="text-white/40" aria-label="إلغاء التعديل" data-testid={`director-cancel-${d.id}`}><X className="w-3 h-3" /></Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0"><Crown className="w-4 h-4 text-amber-400" /></div>
                    <div>
                      <p className="text-white text-sm font-medium">{d.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-white/30">
                        <span dir="ltr">{d.username}</span>
                        {d.directorate_name && <span className="text-amber-400">{d.directorate_name}</span>}
                        {d.phone && <span>{d.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditId(d.id); setForm({ name: d.name || '', password: '', directorate_id: d.directorate_id || '', phone: d.phone || '' }); }} className="p-1.5 rounded-lg hover:bg-white/10 text-white/30 hover:text-white" aria-label="تعديل حساب مدير المديرية" data-testid={`director-edit-${d.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(d.id, d.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {directors.length === 0 && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لا يوجد مديرو مديريات</div>}
      </div>
    </div>
  );
}


// ─── Schools Tab ───
function SchoolsTab({ headers }) {
  const [schools, setSchools] = useState([]);
  const [directorates, setDirectorates] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '', directorate_id: '' });

  const fetch = useCallback(() => {
    axios.get(`${API_URL}/api/admin/schools`, { headers }).then(r => setSchools(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/directorates`, { headers }).then(r => setDirectorates(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    await axios.post(`${API_URL}/api/admin/schools`, form, { headers });
    toast.success('تمت إضافة المدرسة');
    setShowAdd(false);
    setForm({ name: '', address: '', phone: '', directorate_id: '' });
    fetch();
  };

  const handleUpdate = async (id) => {
    await axios.put(`${API_URL}/api/admin/schools/${id}`, form, { headers });
    toast.success('تم التحديث');
    setEditId(null);
    fetch();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`حذف "${name}"؟`)) return;
    await axios.delete(`${API_URL}/api/admin/schools/${id}`, { headers });
    toast.success('تم الحذف');
    fetch();
  };

  return (
    <div className="space-y-4" data-testid="admin-schools">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#4da6ff] flex items-center gap-2"><School className="w-5 h-5" /> المدارس</h2>
        <Button onClick={() => { setShowAdd(true); setForm({ name: '', address: '', phone: '', directorate_id: '' }); }} className="bg-[#4da6ff] hover:bg-[#3d96ef]" data-testid="add-school-btn">
          <Plus className="w-4 h-4 ml-1" /> إضافة مدرسة
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-white/5 border-[#4da6ff]/30">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-white text-sm">إضافة مدرسة جديدة</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input placeholder="اسم المدرسة" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="school-name" />
              <select value={form.directorate_id} onChange={e => setForm(f => ({ ...f, directorate_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="school-directorate">
                <option value="">اختر المديرية</option>
                {directorates.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <input placeholder="العنوان" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
              <input placeholder="رقم الهاتف" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} className="bg-emerald-500"><Check className="w-3 h-3 ml-1" />إضافة</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {schools.map(s => (
          <Card key={s.id} className="bg-white/5 border-white/10">
            <CardContent className="p-3">
              {editId === s.id ? (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                  <select value={form.directorate_id} onChange={e => setForm(f => ({ ...f, directorate_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none">
                    <option value="">بدون مديرية</option>
                    {directorates.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="العنوان" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleUpdate(s.id)} className="bg-emerald-500 text-xs"><Check className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="text-white/40"><X className="w-3 h-3" /></Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-medium">{s.name}</p>
                    <div className="flex items-center gap-3 text-[10px] text-white/30">
                      {s.directorate_name && <span className="text-purple-400">{s.directorate_name}</span>}
                      {s.address && <span>{s.address}</span>}
                      {s.phone && <span>{s.phone}</span>}
                      <span className="text-[#4da6ff]">{s.teacher_count} معلم</span>
                      <span className="text-emerald-400">{s.student_count} طالب</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditId(s.id); setForm({ name: s.name, address: s.address || '', phone: s.phone || '', directorate_id: s.directorate_id || '' }); }} className="p-1.5 rounded-lg hover:bg-white/10 text-white/30 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(s.id, s.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {schools.length === 0 && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لا توجد مدارس</div>}
      </div>
    </div>
  );
}

// ─── Teachers Tab ───
function TeachersTab({ headers }) {
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', name: '', school_id: '', phone: '', grade_ids: [], subject_ids: [] });

  const fetchAll = useCallback(() => {
    axios.get(`${API_URL}/api/admin/teachers`, { headers }).then(r => setTeachers(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/schools`, { headers }).then(r => setSchools(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/grades`, { headers }).then(r => setGrades(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/subjects`, { headers }).then(r => setSubjects(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => setForm({ username: '', password: '', name: '', school_id: '', phone: '', grade_ids: [], subject_ids: [] });

  const toggleGrade = (gid) => {
    setForm(f => ({ ...f, grade_ids: f.grade_ids.includes(gid) ? f.grade_ids.filter(x => x !== gid) : [...f.grade_ids, gid] }));
  };
  const toggleSubject = (sid) => {
    setForm(f => ({ ...f, subject_ids: f.subject_ids.includes(sid) ? f.subject_ids.filter(x => x !== sid) : [...f.subject_ids, sid] }));
  };

  // Filter subjects by selected grades
  const filteredSubjects = subjects.filter(s => form.grade_ids.length === 0 || form.grade_ids.includes(s.grade_id));

  const handleAdd = async () => {
    if (!form.username.trim() || !form.password.trim() || !form.name.trim()) { toast.error('جميع الحقول مطلوبة'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/teachers`, form, { headers });
      toast.success('تمت إضافة المعلم');
      setShowAdd(false);
      resetForm();
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'خطأ'); }
  };

  const handleUpdate = async (id) => {
    const payload = { name: form.name, school_id: form.school_id, phone: form.phone, grade_ids: form.grade_ids, subject_ids: form.subject_ids };
    if (form.password.trim()) payload.password = form.password.trim();
    try {
      await axios.put(`${API_URL}/api/admin/teachers/${id}`, payload, { headers });
      toast.success('تم التحديث'); setEditId(null); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'تعذر حفظ التعديلات'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`حذف المعلم "${name}"؟`)) return;
    await axios.delete(`${API_URL}/api/admin/teachers/${id}`, { headers });
    toast.success('تم الحذف');
    fetchAll();
  };

  const GradeSubjectPicker = () => (
    <div className="col-span-full space-y-2">
      <p className="text-white/50 text-xs">الصفوف التي يدرسها:</p>
      <div className="flex flex-wrap gap-1.5">
        {grades.map(g => (
          <button key={g.id} type="button" onClick={() => toggleGrade(g.id)}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all border ${
              form.grade_ids.includes(g.id) ? 'bg-[#4da6ff]/20 border-[#4da6ff]/50 text-[#4da6ff]' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
            }`}>{g.name_ar}</button>
        ))}
      </div>
      {form.grade_ids.length > 0 && (
        <>
          <p className="text-white/50 text-xs mt-2">المواد التي يدرسها:</p>
          <div className="flex flex-wrap gap-1.5">
            {filteredSubjects.map(s => {
              const grade = grades.find(g => g.id === s.grade_id);
              return (
                <button key={s.id} type="button" onClick={() => toggleSubject(s.id)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all border ${
                    form.subject_ids.includes(s.id) ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                  }`}>{s.name_ar}{grade ? ` (${grade.number})` : ''}</button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-teachers">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#4da6ff] flex items-center gap-2"><UserCheck className="w-5 h-5" /> المعلمون</h2>
        <Button onClick={() => { setShowAdd(true); resetForm(); }} className="bg-[#4da6ff] hover:bg-[#3d96ef]" data-testid="add-teacher-btn">
          <Plus className="w-4 h-4 ml-1" /> إضافة معلم
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-white/5 border-[#4da6ff]/30">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-white text-sm">إضافة معلم جديد</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <input placeholder="رقم المعلم الفريد" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="teacher-username" />
              <input type="password" autoComplete="new-password" placeholder="كلمة المرور" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="teacher-password" />
              <input placeholder="اسم المعلم" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="teacher-name" />
              <select value={form.school_id} onChange={e => setForm(f => ({ ...f, school_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="teacher-school">
                <option value="">اختر المدرسة</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input placeholder="رقم الهاتف" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
              <GradeSubjectPicker />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} className="bg-emerald-500"><Check className="w-3 h-3 ml-1" />إضافة</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {teachers.map(t => (
          <Card key={t.id} className="bg-white/5 border-white/10" data-testid={`teacher-account-${t.id}`}>
            <CardContent className="p-3">
              {editId === t.id ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="الاسم" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" data-testid={`teacher-name-edit-${t.id}`} />
                    <PasswordChangeField key={t.id} value={form.password} onChange={password => setForm(f => ({ ...f, password }))} testId={`teacher-password-${t.id}`} />
                    <select value={form.school_id} onChange={e => setForm(f => ({ ...f, school_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none">
                      <option value="">اختر المدرسة</option>
                      {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="الهاتف" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                  </div>
                  <GradeSubjectPicker />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleUpdate(t.id)} className="bg-emerald-500 text-xs" data-testid={`teacher-save-${t.id}`}><Check className="w-3 h-3 ml-1" />حفظ</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="text-white/40 text-xs" data-testid={`teacher-cancel-${t.id}`}><X className="w-3 h-3 ml-1" />إلغاء</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                      <GraduationCap className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{t.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-white/30 flex-wrap">
                        <span dir="ltr">{t.username}</span>
                        {t.school_name && <span className="text-[#4da6ff]">{t.school_name}</span>}
                        <span className="text-emerald-400">{t.student_count} طالب</span>
                      </div>
                      {(t.grade_names?.length > 0 || t.subject_names?.length > 0) && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {t.grade_names?.map((g, i) => (
                            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-[#4da6ff]/10 text-[#4da6ff]/70 border border-[#4da6ff]/20">{g}</span>
                          ))}
                          {t.subject_names?.map((s, i) => (
                            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400/70 border border-emerald-500/20">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => { setEditId(t.id); setForm({ name: t.name || '', password: '', school_id: t.school_id || '', phone: t.phone || '', grade_ids: t.grade_ids || [], subject_ids: t.subject_ids || [] }); }} className="p-1.5 rounded-lg hover:bg-white/10 text-white/30 hover:text-white" aria-label="تعديل حساب المعلم" data-testid={`teacher-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(t.id, t.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {teachers.length === 0 && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لا يوجد معلمون</div>}
      </div>
    </div>
  );
}


// ─── Principals Tab ───
function PrincipalsTab({ headers }) {
  const [principals, setPrincipals] = useState([]);
  const [schools, setSchools] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', name: '', school_id: '', phone: '' });

  const fetchAll = useCallback(() => {
    axios.get(`${API_URL}/api/admin/principals`, { headers }).then(r => setPrincipals(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/schools`, { headers }).then(r => setSchools(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAdd = async () => {
    if (!form.username.trim() || !form.password.trim() || !form.name.trim()) { toast.error('جميع الحقول مطلوبة'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/principals`, form, { headers });
      toast.success('تمت إضافة المدير');
      setShowAdd(false); setForm({ username: '', password: '', name: '', school_id: '', phone: '' }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'خطأ'); }
  };

  const handleUpdate = async (id) => {
    const payload = { name: form.name, school_id: form.school_id, phone: form.phone };
    if (form.password.trim()) payload.password = form.password.trim();
    try {
      await axios.put(`${API_URL}/api/admin/principals/${id}`, payload, { headers });
      toast.success('تم التحديث'); setEditId(null); fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'تعذر حفظ التعديلات'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`حذف المدير "${name}"؟`)) return;
    await axios.delete(`${API_URL}/api/admin/principals/${id}`, { headers });
    toast.success('تم الحذف'); fetchAll();
  };

  return (
    <div className="space-y-4" data-testid="admin-principals">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#4da6ff] flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> مدراء المدارس</h2>
        <Button onClick={() => { setShowAdd(true); setForm({ username: '', password: '', name: '', school_id: '', phone: '' }); }} className="bg-[#4da6ff] hover:bg-[#3d96ef]" data-testid="add-principal-btn">
          <Plus className="w-4 h-4 ml-1" /> إضافة مدير
        </Button>
      </div>

      {showAdd && (
        <Card className="bg-white/5 border-[#4da6ff]/30">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-bold text-white text-sm">إضافة مدير جديد</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <input placeholder="رقم المدير الفريد" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="principal-username" />
              <input type="password" autoComplete="new-password" placeholder="كلمة المرور" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="principal-password" />
              <input placeholder="اسم المدير" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="principal-name" />
              <select value={form.school_id} onChange={e => setForm(f => ({ ...f, school_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="principal-school">
                <option value="">اختر المدرسة</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input placeholder="رقم الهاتف (اختياري)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} className="bg-emerald-500"><Check className="w-3 h-3 ml-1" />إضافة</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50"><X className="w-3 h-3 ml-1" />إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {principals.map(p => (
          <Card key={p.id} className="bg-white/5 border-white/10" data-testid={`principal-account-${p.id}`}>
            <CardContent className="p-3">
              {editId === p.id ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="الاسم" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" data-testid={`principal-name-edit-${p.id}`} />
                  <PasswordChangeField key={p.id} value={form.password} onChange={password => setForm(f => ({ ...f, password }))} testId={`principal-password-${p.id}`} />
                  <select value={form.school_id} onChange={e => setForm(f => ({ ...f, school_id: e.target.value }))} className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none">
                    <option value="">اختر المدرسة</option>
                    {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="الهاتف" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none" />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleUpdate(p.id)} className="bg-emerald-500 text-xs" aria-label="حفظ التعديلات" data-testid={`principal-save-${p.id}`}><Check className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="text-white/40" aria-label="إلغاء التعديل" data-testid={`principal-cancel-${p.id}`}><X className="w-3 h-3" /></Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center shrink-0"><ShieldCheck className="w-4 h-4 text-indigo-400" /></div>
                    <div>
                      <p className="text-white text-sm font-medium">{p.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-white/30">
                        <span dir="ltr">{p.username}</span>
                        {p.school_name && <span className="text-indigo-400">{p.school_name}</span>}
                        {p.phone && <span>{p.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditId(p.id); setForm({ name: p.name || '', password: '', school_id: p.school_id || '', phone: p.phone || '' }); }} className="p-1.5 rounded-lg hover:bg-white/10 text-white/30 hover:text-white" aria-label="تعديل حساب مدير المدرسة" data-testid={`principal-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(p.id, p.name)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {principals.length === 0 && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لا يوجد مدراء</div>}
      </div>
    </div>
  );
}
